import Split from './Split'
import Tree from './tree'
// Remove lodash cloneDeep since we will use our own tree clone function
import { toRaw } from 'vue'
import './Layout.css'
import './ResponsiveStyles.css'

//  This is a conversion that was made from purejs to react and now in vue
//
// I know its not ideal to mess with the DOM, but it doesn't seem to exist a
// clean way to reparent components in react/vue without losing DOM state.
// My objective here was keep updating views either in DOM (e.g input box) or
// simple on a component as shown on <Clock /> example
//
// How this works:
//  * we receive a list of components that can be usable as views
//  * The state contains a tree with nodes pointing to the viewId
//  * render() will render all views inside a hidden DOM element with
// specific DOM properties and will render the tree elements with designated
// props
//  * componentDidUpdate, componentDidMount grab all views from hidden element
//  and place it on respective tree components as soon as we render(again) the
//  view components are moved back to the original place
//

// DOM
function checkAttach (targetDom, e, isMobile, amount) {
  amount = amount || 33
  var size = amount / 100

  var trect = targetDom.getBoundingClientRect()
  
  const clientX = e.clientX
  const clientY = e.clientY

  if (isMobile) {
    // On mobile, only allow top (0) or bottom (2) rearrange
    const rY = clientY - trect.top
    return (rY < trect.height / 2) ? 0 : 2
  }

  var tW = trect.width * size
  var tH = trect.height * size
  var rPos = {x: clientX - trect.left, y: clientY - trect.top}

  // Calc dists and check the closest one
  var pos = [
    rPos.y - tH,
    (trect.width - tW) - rPos.x,
    (trect.height - tH) - rPos.y,
    rPos.x - tW
  ]
  // only matches if less than 0
  var min = 0
  var minI = -1
  pos.forEach((v, i) => {
    if (v < min) {
      min = v
      minI = i
    }
  })
  return minI
}

// Deep clone function specifically for the flat Tree structure.
// Safely preserves object identity for the `parent` pointers without Proxy issues.
function cloneTreeNodes (nodes) {
  const cloned = nodes.map(n => ({ ...toRaw(n) }))
  for (const node of cloned) {
    if (node.parent) {
      node.parent = cloned.find(n => n.id === node.parent.id)
    }
  }
  return cloned
}

// Vue 3: plain component options object (no Vue.component global registration)
export default {
  name: 'Layout',
  props: {
    'edit': {type: Boolean, default: true},
    'resize': {type: Boolean, default: true},
    'splits': {type: [String, Number, Object], default: () => ({})},
    'responsive': {type: Boolean, default: false},
    'breakpoint': {type: Number, default: 768}
  },
  data () {
    return {
      state: {
        nodes: this.calcSplits(),
        isMobile: false
      },
      // Active drag state
      drag: null
    }
  },
  watch: {
    splits () {
      this.state.nodes = this.calcSplits()
    }
  },
  mounted () {
    // Initial reparenting: move slot children from the hidden staging area
    // into the visible [target-view] containers.
    this.reparentToTargets()

    if (this.responsive) {
      this.observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.state.isMobile = entry.contentRect.width < this.breakpoint
        }
      })
      this.observer.observe(this.$refs.container)

      // Initial check - combine container width and window width for robustness
      const checkInitial = () => {
        const containerWidth = this.$refs.container ? this.$refs.container.clientWidth : 0
        this.state.isMobile = (containerWidth > 0 && containerWidth < this.breakpoint) || window.innerWidth < this.breakpoint
      }
      checkInitial()
      // Re-check after a tiny delay to catch emulation mode shifts
      setTimeout(checkInitial, 100)

      this.resizeHandler = checkInitial
      window.addEventListener('resize', this.resizeHandler)
    }
  },
  unmounted () {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  },
  updated () {
    // After every re-render, move children from staging back into targets.
    this.reparentToTargets()
  },
  beforeUpdate () {
    // Before re-render, move children back to staging so Vue's diff sees
    // the staging area populated (matching the VDOM) and leaves targets empty.
    if (!this.$refs.container) { return }
    var els = this.$refs.container.querySelectorAll('[target-view]')
    Array.from(els).forEach((e) => {
      if (!e.children[0]) return
      var el = this.$refs.container.querySelector('[src-view="' + e.getAttribute('target-view') + '"]')
      if (el) el.appendChild(e.children[0])
    })
  },
  methods: {
    // Move slot children from the hidden [src-view] staging area into the
    // visible [target-view] containers. Called after mount and after every
    // update.
    reparentToTargets () {
      if (!this.$refs.container) return
      this.$emit('layout:begin')
      const els = this.$refs.container.querySelectorAll('[target-view]')
      Array.from(els).forEach((e) => {
        const targetName = e.getAttribute('target-view')
        const srcView = this.$refs.container.querySelector('[src-view="' + targetName + '"]')
        if (!srcView) return
        if (!srcView.children[0]) return
        e.appendChild(srcView.children[0])
      })
      this.$emit('layout:complete')
    },
    // Transform input into internal format
    calcSplits () {
      const root = []
      const tree = Tree.from(root)
      const walk = (node) => {
        // See node check if object or viewId
        if (node instanceof Object) {
          let split = tree.push({type: 'split', dir: node.dir, split: node.split})
          walk(node.first).parent = split
          walk(node.second).parent = split
          return split
        }
        return tree.push({type: 'view', viewId: node})
      }
      walk(this.splits)

      return root
    },
    onSplitResize (e, split, size) {
      // split is the Split component instance (this) passed via $emit.
      // node-id is not a declared prop on Split so it lives in $attrs.
      const nodeId = parseInt(split.$attrs['node-id'], 10)
      const node = Tree.from(this.state.nodes).findById(nodeId)
      if (node) {
        node.split = size
      }
    },

    // ─── Public API ──────────────────────────────────────────────────────────

    // Add a pane (identified by viewId) to the layout, placed next to
    // targetViewId at the given position (0=top 1=right 2=bottom 3=left).
    // size: percentage the new pane takes (default 50).
    // If targetViewId is omitted the pane is added next to the first view.
    addPane (viewId, targetViewId, position, size) {
      const tree = Tree.from(this.state.nodes)
      // Resolve target: find by viewId or fall back to the first view node
      let target = targetViewId !== undefined
        ? tree.findByViewId(targetViewId)
        : this.state.nodes.find(n => n.type === 'view')
      if (!target) {
        // Tree is empty — just push a single root view node
        const node = tree.push({ type: 'view', viewId })
        this.$emit('pane:added', viewId)
        return node
      }
      const added = tree.addByViewId(viewId, target.viewId, position, size)
      this.$emit('pane:added', viewId)
      return added
    },

    // Remove a pane by viewId. Returns true if removed.
    removePane (viewId) {
      const tree = Tree.from(this.state.nodes)
      const removed = tree.removeByViewId(viewId)
      if (removed) {
        this.$emit('pane:removed', viewId)
      }
      return removed
    },
    previewPane (attach, targetDom, amount) {
      if (attach === -1) {
        this.$refs.preview.style.opacity = 0
        return
      }
      if (targetDom === undefined) {
        return -1
      }
      amount = amount || 33 // default 33%
      const size = amount / 100

      // Precalc styles
      const targetRect = targetDom.getBoundingClientRect()
      const previewPos = {
        left: targetRect.left,
        top: targetRect.top,
        width: targetRect.width,
        height: targetRect.height
      }

      if (attach === 1) {
        previewPos.left += previewPos.width - previewPos.width * size
      } else if (attach === 2) {
        previewPos.top += previewPos.height - previewPos.height * size
      }
      if ((attach % 2) === 0) {
        previewPos.height *= size
      } else if ((attach % 2) === 1) {
        previewPos.width *= size
      }
      // Update DOM style
      this.$refs.preview.style.opacity = 1
      this.$refs.preview.style.position = 'fixed'
      for (const k in previewPos) {
        this.$refs.preview.style[k] = previewPos[k] + 'px'
      }
    },
    onViewDragStart (e) {
      if (this.drag) return
      // Ignore right clicks or auxiliary clicks
      if (e.button !== 0) return

      // Dynamic check to ensure isMobile is absolutely in sync with the viewport on drag start
      if (this.responsive) {
        const containerWidth = this.$refs.container ? this.$refs.container.clientWidth : 0
        this.state.isMobile = (containerWidth > 0 && containerWidth < this.breakpoint) || window.innerWidth < this.breakpoint
      }

      const dragHandle = e.target.closest('[pane-drag]')
      // On mobile, ONLY allow dragging if explicitly from a drag handle
      if (this.state.isMobile && !dragHandle) return

      const viewDom = e.target.closest('[node-id]')
      if (!viewDom) return

      // Do not initiate drag if clicking on interactive elements (buttons, inputs, links, etc.)
      const interactive = e.target.closest('button, input, select, textarea, a, [role="button"]')
      if (interactive) return

      const nodeId = parseInt(viewDom.getAttribute('node-id'), 10)
      const node = Tree.from(this.state.nodes).findById(nodeId)
      if (!node) return

      if (e.cancelable) e.preventDefault()
      e.stopPropagation()

      const trect = viewDom.getBoundingClientRect()
      const clientX = e.clientX
      const clientY = e.clientY
      const containerRect = this.$refs.container.getBoundingClientRect()
      const scrollTop = this.$refs.container ? this.$refs.container.scrollTop : 0
      const scrollLeft = this.$refs.container ? this.$refs.container.scrollLeft : 0

      console.log('[layout] drag start', { nodeId, isMobile: this.state.isMobile })

      this.drag = {
        node: node,
        offset: { x: clientX - trect.left, y: clientY - trect.top },
        x: trect.left - containerRect.left + scrollLeft,
        y: trect.top - containerRect.top + scrollTop,
        w: trect.width,
        h: trect.height,
        over: null
      }

      this.state.savedNodes = cloneTreeNodes(this.state.nodes)
      if (!this.state.isMobile) {
        Tree.from(this.state.nodes).removeChild(node)
      }

      document.addEventListener('pointermove', this.onViewDrag)
      document.addEventListener('pointerup', this.onViewDrop)
      document.addEventListener('pointercancel', this.onViewDrop)
    },

    onViewDrag (e) {
      if (!this.drag) return
      if (e.cancelable) e.preventDefault()
      e.stopPropagation()

      const clientX = e.clientX
      const clientY = e.clientY

      const containerRect = this.$refs.container.getBoundingClientRect()
      const scrollTop = this.$refs.container ? this.$refs.container.scrollTop : 0
      const scrollLeft = this.$refs.container ? this.$refs.container.scrollLeft : 0
      
      // Update ghost position in state for reactive rendering
      this.drag.x = clientX - containerRect.left - this.drag.offset.x + scrollLeft
      this.drag.y = clientY - containerRect.top - this.drag.offset.y + scrollTop

      // Disable drag ghost temporarily to get element below
      const dragEl = this.$refs.drag
      if (dragEl) dragEl.style.pointerEvents = 'none'
      const el = document.elementFromPoint(clientX, clientY)
      if (dragEl) dragEl.style.pointerEvents = ''

      // Find parent view element
      let viewDom = el
      for (; viewDom && viewDom.matches && !viewDom.matches('.view'); viewDom = viewDom.parentNode) {}
      
      if (!viewDom || !viewDom.matches) {
        this.previewPane(-1)
        this.drag.over = null
        return
      }

      const attach = checkAttach(viewDom, e, this.state.isMobile)
      if (attach !== -1) {
        this.drag.over = { viewDom, attach }
      } else {
        this.drag.over = null
      }
      this.previewPane(attach, viewDom)
    },
    onViewDrop (e) {
      if (!this.drag) return
      
      console.log('[layout] drag drop', { over: !!this.drag.over })

      document.removeEventListener('pointermove', this.onViewDrag)
      document.removeEventListener('pointerup', this.onViewDrop)
      document.removeEventListener('pointercancel', this.onViewDrop)

      this.previewPane(-1)

      if (this.drag.over == null) {
        this.state.nodes = this.state.savedNodes // rollback
        this.drag = null
        return
      }

      const { viewDom, attach } = this.drag.over
      const nodeId = parseInt(viewDom.getAttribute('node-id'), 10)
      const tree = Tree.from(this.state.nodes)
      const node = tree.findById(nodeId)
      
      if (node) {
        if (this.state.isMobile) {
          // On mobile, the node was not removed from the active tree on drag start.
          // Remove it now, then find the target node again in the mutated tree.
          tree.removeChild(this.drag.node)
          const freshTargetNode = tree.findById(nodeId)
          if (freshTargetNode) {
            tree.attachChild(freshTargetNode, attach, this.drag.node)
          } else {
            this.state.nodes = this.state.savedNodes // rollback
          }
        } else {
          tree.attachChild(node, attach, this.drag.node)
        }
      } else {
        this.state.nodes = this.state.savedNodes // fallback rollback
      }
      
      this.drag = null
    }
  },

  render () {
    // Reparenting is handled by mounted() and updated() hooks — render
    // function should remain pure.

    // Layout renderer — recursively builds VNode tree from flat node array
    const walk = (node) => {
      switch (node.type) {
        case 'split': {
          const children = Tree.from(this.state.nodes).childrenOf(node).map(k => walk(k))
          // Pass children via the explicit `default` slot function so Vue 3
          // delivers them as a true VNode array (not wrapped in a fragment).
          return (
            <Split
              key={node.id}
              node-id={node.id}
              resizeable={this.resize}
              isMobile={this.state.isMobile}
              dir={node.dir}
              split={node.split}
              onSplitResize={this.onSplitResize}
              v-slots={{ default: () => children }}
            />
          )
        }
        default: {
          const isDraggingThis = this.drag && this.drag.node && this.drag.node.id === node.id
          const viewClass = [
            'view',
            isDraggingThis ? 'dragging-placeholder' : ''
          ].filter(Boolean).join(' ')

          // If this node is being dragged, do not set target-view so that the DOM children
          // are reparented to the drag ghost instead of the placeholder.
          const targetView = isDraggingThis ? null : 'view-' + node.viewId

          if (this.edit) {
            return (
              <div 
                class={viewClass} 
                node-id={node.id} 
                target-view={targetView} 
                onPointerdown={this.onViewDragStart}
              ></div>
            )
          }
          return (<div class={viewClass} node-id={node.id} target-view={targetView}></div>)
        }
      }
    }

    const tree = this.state.nodes[0] ? walk(this.state.nodes[0]) : null
    const layoutClass = [
      'layout-container',
      this.edit ? 'edit' : '',
      this.state.isMobile ? 'mobile-mode' : ''
    ]

    // Vue 3: $slots.default is a function, not an array.
    // Flatten Fragment VNodes (e.g. produced by v-for) so dynamically added
    // children are seen as individual slot entries.
    const rawSlots = this.$slots.default ? this.$slots.default() : []
    const flatten = (nodes) => {
      const out = []
      for (const v of nodes) {
        if (!v) continue
        // A Fragment VNode has type === Fragment (a Symbol) and its children
        // is an array of the actual VNodes (e.g. v-for output).
        if (typeof v.type === 'symbol' && Array.isArray(v.children)) {
          out.push(...flatten(v.children))
        } else if (v.type !== undefined && typeof v.type !== 'symbol') {
          // Real element/component VNode
          out.push(v)
        }
        // else: text/comment VNodes are dropped
      }
      return out
    }
    const slotNodes = flatten(rawSlots)

    return (
      <div class={layoutClass.join(' ')} ref="container">
        <div class={'views ' + (this.drag ? 'dragging' : '')} ref="views">
          {tree}
        </div>
        <div class="preview" ref="preview"></div>
        <div
          class={'drag ' + (this.drag ? 'dragging' : '')}
          ref="drag"
          style={{
            left: this.drag ? this.drag.x + 'px' : '0px',
            top: this.drag ? this.drag.y + 'px' : '0px',
            width: this.drag ? this.drag.w + 'px' : '0px',
            height: this.drag ? this.drag.h + 'px' : '0px',
            transformOrigin: this.drag ? (this.drag.offset.x + 'px ' + this.drag.offset.y + 'px') : ''
          }}>
          {
            this.drag &&
            <div
              class="view"
              target-view={'view-' + this.drag.node.viewId}
            />
          }
        </div>
        <div style={{display: 'none'}}>
          {slotNodes.map((view, i) => {
            return (<div key={view.key || i} src-view={'view-' + (view.key || i)}>{view}</div>)
          })}
        </div>
      </div>
    )
  }
}
